import { css } from "@emotion/react"
import styled from "@emotion/styled"
import Button from "components/button/Button"
import { Icon } from "components/icon/Icon"
import { cssVars } from "components/nav-v2/Wrapper"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { a11yOutline } from "css/a11y"
import getMediaQuery from "css/breakpoints"
import { bold, legend100 } from "css/text"
import { useMotionValue, useMotionValueEvent } from "framer-motion"
import { useLayoutEffect, useRef } from "react"

const Root = styled.div`
  --hide: 0;
  position: fixed;
  z-index: 9990;
  top: calc((var(${cssVars.main.safe}) * var(${cssVars.main.bar.pinned}) + 1.38rem) - 100% * max(0, var(${cssVars.main.lock})));
  inset-inline-start: var(--outer-margin);
  background: rgb(var(--pure-white) / 0.85);
  border-radius: 0.38rem;
  box-shadow: 0 4px 28px 0 rgba(0, 0, 0, 0.1);
  transform: translate3d(0, calc(var(--hide) * -100% - var(--hide) * 1.38rem), 0);
  transition: transform 400ms;

  &:has(button.focus-visible) {
    ${a11yOutline}
  }
`

const backLinkStyles = css`
  white-space: nowrap !important;
  padding-block: 0.5rem !important;
  padding-inline: 0.88rem !important;

  height: unset;
  display: flex !important;

  &:hover {
    color: rgb(var(--green)) !important;
  }

  & > svg.icon {
    margin-inline: 0 var(--gap) !important;
  }

  html:not([dir="rtl"]) & svg {
    transform: scaleX(-1);
  }

  & > span.link-content {
    ${legend100}
    ${bold}
    line-height: 1rem;
    clip-path: initial;
    height: initial;
    overflow: initial;
    position: initial;
    white-space: nowrap;
    width: initial;
    display: none;
  }

  ${getMediaQuery("m")} {
    & > span.link-content {
      display: initial;
    }
  }
`

const BackButton = styled(Button)`
  &.focus-visible {
    outline: none !important;
  }
  ${backLinkStyles}

  // show "back" label all the time
	& > span.link-content {
    display: initial;
  }
`
const Back = () => {
  const console = useConsole()
  const navigation = useNavigation()
  const dictionary = useDictionary()
  const rroot = useRef(null)

  const hide = useMotionValue(0)
  useMotionValueEvent(hide, "change", latest => {
    if (rroot.current) {
      rroot.current.style.setProperty("--hide", latest)
    }
  })

  useLayoutEffect(() => {
    const controller = new AbortController()
    const root = rroot.current
    const footer = document.querySelector("footer .sections")
    let intersectionObserver = null

    if (footer && root) {
      const onresize = () => {
        const innerHeight = window.innerHeight
        const nodeHeight = root.clientHeight
        const headerSafe = getComputedStyle(document.documentElement)?.getPropertyValue(cssVars.main.nsafe)
        const ioMargin = innerHeight - headerSafe - nodeHeight * 2

        if (intersectionObserver) intersectionObserver.disconnect()
        intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            hide.set(+entry.isIntersecting)
          },
          { rootMargin: `0px 0px ${-ioMargin}px 0px` }
        )
        intersectionObserver.observe(footer)
      }
      global.addEventListener("resize", onresize, { signal: controller.signal })
      onresize()
    }

    return () => {
      controller.abort()
      if (intersectionObserver) intersectionObserver.disconnect()
    }
  })

  console.verbose("Back(%o)", { canGoBack: navigation.canGoBack })
  return navigation.canGoBack ? (
    <Root ref={rroot}>
      <BackButton type='button' onPress={() => navigation.back()} icon='none' className='inline'>
        <Icon type='arrowBack' className='icon' />
        <span className='link-content'>{dictionary.back()}</span>
      </BackButton>
    </Root>
  ) : null
}

export default Back
